import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDifferenceViewOptions, getInspectionId, inspection } from 'store';
import { updateDifferenceViewOptions } from 'store/request/inspections/actions';
import { ContentDifferences, DifferenceTypeNames, FormattingDifferences } from 'types';
import useDebounce from 'utils/useDebounce';

// we filter by the difference type, but some differences have the same displayname
// ex. superscript and subscript have displayname sub/super, so subscript controls both superscript and subscript
export const filterTypeMappings: any = {
  [ContentDifferences.Hyphen]: ContentDifferences.Change,
  [FormattingDifferences.Superscript]: FormattingDifferences.Subscript,
  [FormattingDifferences.Size]: FormattingDifferences.FontSize,
};

export const useFilters = () => {
  const { differenceTypeFilters, differenceStatusFilters } = useSelector(getDifferenceViewOptions);
  const inspectionId = useSelector(getInspectionId);
  const dispatch = useDispatch();

  useEffect(() => {
    const currentFilters = Object.keys(differenceTypeFilters);
    // if it is a new inspection, or an old inspection with an undeclared type
    if (Object.keys(DifferenceTypeNames).some((type) => !(type in currentFilters))) {
      const initialFilters: any = {};
      Object.values(DifferenceTypeNames).forEach((type) => {
        initialFilters[type] = !!differenceTypeFilters[filterTypeMappings[type] || type];
      });
      dispatch(inspection.actions.setDifferenceViewOptions({ differenceTypeFilters: initialFilters }));
    }
  }, []);

  const debouncedTypeFilters = useDebounce(differenceTypeFilters, 500);

  // "Listen" to filter changes to update the inspection in the backend
  useEffect(() => {
    if (debouncedTypeFilters) {
      dispatch(
        updateDifferenceViewOptions(
          { filterOptions: { ...debouncedTypeFilters, ...differenceStatusFilters } },
          inspectionId,
          'updateFilters',
        ),
      );
    }
  }, [debouncedTypeFilters, differenceStatusFilters]);

  const visibleFilters = Object.keys(differenceTypeFilters)
    .filter((type) => !(type in filterTypeMappings))
    .reduce((filters: any, type) => {
      filters[type] = differenceTypeFilters[type];
      return filters;
    }, {});

  const isTextFilter = (type: string) => type in FormattingDifferences || type in ContentDifferences;

  const toggleFilter = (type: DifferenceTypeNames) => {
    const newValue = !differenceTypeFilters[type];
    const newFilters = { ...differenceTypeFilters, [type]: newValue };
    // if we are changing a filter with two types we should update the other one as well
    const otherType = Object.keys(filterTypeMappings).find((key) => filterTypeMappings[key] === type);
    if (otherType) {
      newFilters[otherType] = newValue;
    }
    dispatch(inspection.actions.setDifferenceViewOptions({ differenceTypeFilters: newFilters }));
  };

  const updateFilters = (types: DifferenceTypeNames[], value: boolean) => {
    const newFilters = { ...differenceTypeFilters };
    types.forEach((type) => {
      newFilters[type] = value;
      if (filterTypeMappings[type]) {
        newFilters[filterTypeMappings[type]] = value;
      }

      // if we are changing a filter with two types we should update the other one as well
      Object.keys(filterTypeMappings).forEach((key) => {
        newFilters[key] = newFilters[filterTypeMappings[key]];
      });
    });
    dispatch(inspection.actions.setDifferenceViewOptions({ differenceTypeFilters: newFilters }));
  };

  const isEveryFilterEnabled = Object.keys(differenceTypeFilters).every(
    (type) => differenceTypeFilters[type] === false,
  );

  const visibleTextFilters = Object.keys(visibleFilters)
    .filter((type) => isTextFilter(type))
    .reduce((res: any, type) => {
      res[type] = visibleFilters[type];
      return res;
    }, {});

  const isEveryTextFilterEnabled = Object.keys(visibleTextFilters).every((type) => !visibleFilters[type]);

  return {
    textFilters: visibleTextFilters,
    typeFilters: visibleFilters,
    statusFilters: differenceStatusFilters,
    toggleFilter,
    updateFilters,
    isEveryTextFilterEnabled,
    isEveryFilterEnabled,
  };
};
